import { alpha, darken } from '@mui/material/styles'

import { TroughLevel } from '#/@types/backend'
import { CompanyRelationshipType } from '#/@types/backend'
import Palette from '#/styles/Palette'

export default {
  animal: {
    adult: {
      backgroundColor: Palette.blue.dark[300],
      color: Palette.blue.dark[500],
    },
    breathing: {
      backgroundColor: Palette.orange.self[500],
      color: Palette.white.self[100],
    },
    discard: {
      backgroundColor: Palette.gray.self[300],
      color: Palette.gray.self[600],
    },
    multiparous: {
      backgroundColor: Palette.blue.marine[500],
      color: Palette.blue.marine[700],
    },
    primiparous: {
      backgroundColor: Palette.blue.light[500],
      color: Palette.blue.light[700],
    },
  },

  batch: {
    backgroundColor: Palette.blue.dark[500],
    color: Palette.white.self[100],
  },

  /**
   * Cor por domínio/categoria (escala `{100: ..., 700: ...}`), compartilhada entre o
   * QuickActions (menu de registro rápido) e o QuickLinks (busca avançada).
   */
  categories: {
    collar: Palette.orange.collar,
    cow_comfort: Palette.blue.cow_comfort,
    exports: Palette.gray.dark,
    handling: Palette.pink.handling,
    health: Palette.yellow.health,
    herd: Palette.green.herd,
    imports: Palette.gray.self,
    nutrition: Palette.green.nutrition,
    production: Palette.blue.production,
    rearing: Palette.pink.rearing,
    reproduction: Palette.purple.reproduction,
  },

  ccs_status: {
    chronic: {
      backgroundColor: Palette.red.light[100],
      color: Palette.red.light[700],
    },
    cured: {
      backgroundColor: Palette.green.mint[100],
      color: Palette.green.mint[700],
    },
    healthy: {
      backgroundColor: Palette.green.primary[100],
      color: Palette.green.primary[900],
    },
    new_infections: {
      backgroundColor: Palette.orange.self[100],
      color: Palette.orange.light[600],
    },
  },
  company_relationship: {
    commercial_representation: {
      backgroundColor: Palette.blue.self[100],
      color: Palette.blue.self[700],
    },
    customer: {
      backgroundColor: Palette.green.self[100],
      color: Palette.green.self[700],
    },
    full_representation: {
      backgroundColor: Palette.purple.dark[100],
      color: Palette.purple.dark[400],
    },
    guest: {
      backgroundColor: Palette.orange.light[100],
      color: Palette.orange.light[600],
    },
  } as Record<CompanyRelationshipType, { backgroundColor: string; color: string }>,

  contract: {
    'active': {
      backgroundColor: Palette.green.self[700],
      color: Palette.white.self[100],
    },
    'active-cancellation-requested': {
      backgroundColor: Palette.green.self[700],
      color: Palette.white.self[100],
    },
    'expired': {
      backgroundColor: Palette.gray.self[400],
      color: Palette.white.self[100],
    },
    'inactive': {
      backgroundColor: Palette.red.light[500],
      color: Palette.white.self[100],
    },
    'suspended': {
      backgroundColor: Palette.orange.light[600],
      color: Palette.white.self[100],
    },
    'suspended-cancellation-requested': {
      backgroundColor: Palette.orange.light[600],
      color: Palette.white.self[100],
    },
    'trial': {
      backgroundColor: Palette.green.light[600],
      color: Palette.white.self[100],
    },
    'waiting': {
      backgroundColor: Palette.orange.self[500],
      color: Palette.white.self[100],
    },
    'waiting-cancellation-requested': {
      backgroundColor: Palette.orange.self[500],
      color: Palette.white.self[100],
    },
  },

  danger: Palette.red.self[400],
  darkerGray: Palette.gray.self[700],
  devices: {
    capsule: {
      black: {
        backgroundColor: Palette.gray.self[300],
        color: Palette.gray.self[900],
      },
      green: {
        backgroundColor: Palette.green.self[100],
        color: Palette.green.self[700],
      },
      pink: {
        backgroundColor: Palette.pink.self[100],
        color: Palette.pink.self[700],
      },
    },
    ctech: {
      normal: {
        backgroundColor: Palette.blue.dark[500],
        color: Palette.white.self[100],
      },
      replace: {
        backgroundColor: Palette.orange.dark[600],
        color: Palette.white.self[100],
      },
      revision: {
        backgroundColor: Palette.orange.self[500],
        color: Palette.white.self[100],
      },
      without_status: {
        backgroundColor: Palette.gray.self[500],
        color: Palette.white.self[100],
      },
    },
  },
  devmode: '#5800CE',
  dtr: {
    intense: Palette.red.self[500],
    moderated: Palette.yellow.self[500],
    normal: Palette.green.self[500],
  },
  error: Palette.red.self[500],
  feedback: {
    danger: {
      backgroundColor: Palette.red.light[100],
      color: Palette.red.light[700],
    },
    error: {
      backgroundColor: Palette.red.light[100],
      color: Palette.red.light[700],
    },
    info: {
      backgroundColor: Palette.blue.self[100],
      color: Palette.blue.self[700],
    },
    neutral: {
      backgroundColor: 'inherit',
      color: Palette.gray.self[800],
    },
    regular: {
      backgroundColor: Palette.gray.self[200],
      color: Palette.gray.self[900],
    },
    success: {
      backgroundColor: Palette.green.mint[100],
      color: Palette.green.mint[700],
    },
    warning: {
      backgroundColor: Palette.orange.self[100],
      color: Palette.orange.light[500],
    },
  },
  health: {
    challenge: {
      backgroundColor: Palette.yellow.self[100],
      color: Palette.yellow.self[700],
      graph: Palette.orange.self[500],
    },
    critical: {
      backgroundColor: Palette.red.self[100],
      color: Palette.red.self[700],
      graph: Palette.red.self[400],
    },
    normal: {
      backgroundColor: Palette.green.self[100],
      color: Palette.green.self[700],
      graph: Palette.green.self[500],
    },
    observation: {
      backgroundColor: Palette.orange.light[100],
      color: Palette.orange.light[600],
      graph: Palette.orange.light[500],
    },
  },

  heat: {
    awaiting: {
      backgroundColor: Palette.orange.self[100],
      color: Palette.orange.self[600],
    },
    verified: {
      backgroundColor: Palette.green.mint[100],
      color: Palette.green.mint[700],
    },
  },

  /** Cor por nível de intensidade de cio, de 1 (fraco) a 5 (forte) */
  heatStrength: {
    1: '#FBCF1A',
    2: '#FBCF1A',
    3: '#5CCB5F',
    4: '#009929',
    5: '#009929',
  },

  inapt: {
    discard: Palette.red.self[400],
    induction: '#739abf',
    unfit: '#ffc107',
  },

  info: Palette.blue.light[400],

  lighterGray: Palette.gray.self[200],

  map: {
    markers: {
      ccom: {
        inactive: Palette.gray.dark[500],
        offline: Palette.red.self[300],
        operational: Palette.green.self[500],
        waiting: Palette.orange.self[400],
      },
    },
  },

  muted: Palette.gray.self[800],

  nps: {
    awaiting: {
      backgroundColor: Palette.orange.self[100],
      color: Palette.orange.self[600],
    },
    // Answer group
    detractor: {
      backgroundColor: Palette.red.light[100],
      color: Palette.red.light[700],
    },
    // Status
    finalized: {
      backgroundColor: Palette.green.mint[100],
      color: Palette.green.mint[700],
    },
    neutral: {
      backgroundColor: Palette.orange.self[100],
      color: Palette.orange.self[600],
    },
    ongoing: {
      backgroundColor: Palette.blue.self[100],
      color: Palette.blue.self[700],
    },
    promotter: {
      backgroundColor: Palette.green.mint[100],
      color: Palette.green.mint[700],
    },
    suspended: {
      backgroundColor: Palette.red.light[100],
      color: Palette.red.light[700],
    },
  },

  plan: {
    cowconnect: {
      backgroundColor: Palette.blue.self[200],
      color: Palette.white.self[100],
    },
    monitoring: {
      backgroundColor: Palette.blue.light[400],
      color: Palette.white.self[100],
    },
  },

  ppi: {
    challenge: Palette.red.self[400],
    normal: Palette.green.self[500],
  },

  /* Basic */
  primary: Palette.green.primary[600],

  production: {
    dry: {
      backgroundColor: Palette.gray.self[200],
      color: Palette.gray.self[600],
    },
    lactating: {
      backgroundColor: Palette.blue.dark[100],
      color: Palette.blue.dark[500],
    },
  },

  protocol: {
    canceled: {
      backgroundColor: Palette.red.light[100],
      color: Palette.red.light[700],
    },
    completed: {
      backgroundColor: Palette.green.mint[100],
      color: Palette.green.mint[700],
    },
    heat_observation: {
      backgroundColor: Palette.purple.dark[100],
      color: Palette.purple.dark[400],
    },
    pre_sync: {
      backgroundColor: Palette.blue.light[100],
      color: Palette.blue.light[600],
    },
    scheduled: {
      backgroundColor: Palette.orange.light[100],
      color: Palette.orange.light[600],
    },
    service: {
      backgroundColor: Palette.purple.reproduction[100],
      color: Palette.purple.reproduction[600],
    },
    sync: {
      backgroundColor: Palette.blue.dark[200],
      color: Palette.blue.dark[600],
    },
  },

  reproduction: {
    birth: {
      backgroundColor: Palette.pink.birth[50],
      color: Palette.pink.birth[700],
    },
    calf: {
      backgroundColor: Palette.pink.self[200],
      color: Palette.pink.self[700],
    },
    calfNewBorn: {
      backgroundColor: Palette.pink.light[200],
      color: Palette.pink.self[600],
    },
    empty: {
      backgroundColor: Palette.green.night[200],
      color: Palette.green.night[700],
    },
    emptyPostDelivery: {
      backgroundColor: Palette.yellow.self[200],
      color: Palette.yellow.self[700],
    },
    heat: {
      backgroundColor: Palette.purple.reproduction[100],
      color: Palette.purple.reproduction[700],
    },
    heifer: {
      backgroundColor: Palette.purple.light[100],
      color: Palette.purple.light[600],
    },
    hormoneImplanted: {
      backgroundColor: Palette.red.peach[200],
      color: Palette.red.light[700],
    },
    implantRemoved: {
      backgroundColor: Palette.red.salmon[200],
      color: Palette.red.self[700],
    },
    inseminated: {
      backgroundColor: Palette.green.self[200],
      color: Palette.green.self[700],
    },
    lateInseminated: {
      backgroundColor: Palette.orange.light[100],
      color: Palette.orange.light[600],
    },
    male: {
      backgroundColor: Palette.blue.zinc[200],
      color: Palette.blue.zinc[700],
    },
    pregnant: {
      backgroundColor: Palette.purple.self[200],
      color: Palette.purple.self[700],
    },
    pregnantPreDelivery: {
      backgroundColor: Palette.purple.dark[100],
      color: Palette.purple.dark[700],
    },
    prostaglandin: {
      backgroundColor: Palette.orange.dark[200],
      color: Palette.orange.light[700],
    },
  },

  /**
   * Cor por categoria da busca avançada (sidebar de filtros + cabeçalho dos grupos de
   * resultado). Escopo próprio, sem relação com `categories` (QuickActions/QuickLinks).
   */
  searchFilters: {
    all: Palette.gray.dark[900],
    analytics: Palette.red.self[400],
    animals: Palette.blue.self[500],
    animals_without_collar: Palette.gray.self[600],
    batches: Palette.purple.self[600],
    collars: Palette.green.primary[600],
    farms: Palette.green.light[700],
    lists: Palette.orange.self[500],
    resources: Palette.gray.self[600],
  },

  sector: {
    cs: '#409916',
    financial: '#804DAD',
    support: '#4078B9',
  },

  services: Palette.purple.dark[300],

  sex: {
    female: {
      backgroundColor: Palette.purple.self[600],
      color: Palette.white.self[100],
    },
    male: {
      backgroundColor: Palette.purple.dark[500],
      color: Palette.white.self[100],
    },
    unknown: {
      backgroundColor: 'transparent',
      color: Palette.gray.light[500],
    },
  },

  status: {
    canceled: {
      backgroundColor: Palette.red.light[100],
      color: Palette.red.light[700],
    },
    completed: {
      backgroundColor: Palette.green.mint[100],
      color: Palette.green.mint[700],
    },
    ongoing: {
      backgroundColor: Palette.blue.self[100],
      color: Palette.blue.self[700],
    },
    rejected: {
      backgroundColor: Palette.red.self[100],
      color: Palette.red.light[600],
    },
    scheduled: {
      backgroundColor: Palette.orange.self[100],
      color: Palette.orange.light[500],
    },
  },

  success: Palette.green.self[500],

  tags: {
    new: {
      backgroundColor: Palette.green.self[100],
      color: Palette.green.self[700],
    },
  },

  theme: {
    colors: {
      baby: {
        contrastText: Palette.white.self[100],
        dark: Palette.pink.dark[700],
        light: Palette.pink.light[700],
        main: Palette.pink.self[700],
      },
      error: {
        main: Palette.red.light[700],
      },
      info: {
        dark: Palette.blue.self[600],
        light: Palette.blue.self[400],
        main: Palette.blue.self[700],
      },
      neutral: {
        contrastText: Palette.gray.dark[100],
        dark: Palette.gray.self[400],
        light: Palette.white.self[100],
        main: Palette.white.self[100],
      },
      primary: {
        dark: Palette.green.primary[900],
        light: Palette.green.primary[300],
        main: Palette.green.primary[600],
      },
      regular: {
        main: Palette.gray.self[900],
      },
      secondary: {
        dark: Palette.gray.dark[100],
        light: Palette.gray.light[400],
        main: Palette.gray.self[600],
      },
      success: {
        dark: Palette.green.primary[900],
        light: Palette.green.primary[300],
        main: Palette.green.primary[600],
      },
      text: {
        main: Palette.gray.dark[900],
        primary: Palette.gray.dark[900],
        secondary: Palette.gray.self[500],
      },
      warning: {
        dark: Palette.orange.self[500],
        light: Palette.orange.self[200],
        main: Palette.orange.light[500],
      },
      white: {
        contrastText: Palette.black.self[100],
        dark: Palette.gray.self[400],
        light: Palette.white.self[100],
        main: Palette.white.self[100],
      },
    },
  },
  thi: {
    // 72 to 79
    mild_to_moderate_stress: {
      backgroundColor: Palette.orange.brick[100],
      chip: Palette.orange.brick[400],
      color: Palette.orange.brick[700],
      plotband: Palette.orange.collar[500],
    },
    // 80 to 89
    moderate_to_severe_stress: {
      backgroundColor: Palette.red.salmon[100],
      chip: Palette.red.salmon[400],
      color: Palette.red.salmon[700],
      plotband: Palette.red.self[400],
    },
    // 90 to 100
    severe_stress: {
      backgroundColor: Palette.red.peach[100],
      chip: Palette.red.peach[400],
      color: Palette.red.peach[700],
      plotband: Palette.red.self[500],
    },
    // 68 to 71
    stress_zone: {
      backgroundColor: Palette.orange.sand[100],
      chip: Palette.orange.sand[400],
      color: Palette.orange.sand[700],
      plotband: Palette.orange.collar[400],
    },
    // 50 to 67
    thermal_comfort: {
      backgroundColor: Palette.blue.sky[100],
      chip: Palette.blue.sky[400],
      color: Palette.blue.sky[700],
      plotband: Palette.white.self[100],
    },
  },
  trough_level: {
    trough_empty: Palette.red.self[200],
    trough_full: Palette.green.self[300],
    trough_low: Palette.yellow.self[300],
  } as Record<TroughLevel, string>,
  variation: {
    deteriorate: {
      color: Palette.red.self[700],
    },
    improve: {
      color: Palette.green.self[700],
    },
  },
  vic: {
    action: {
      accent: Palette.green.light[500],
      message: Palette.green.light[100],
    },
    baby: {
      accent: Palette.pink.self[500],
      message: Palette.pink.light[200],
    },
    conversationList: {
      background: Palette.green.primary[600],
      divider: Palette.gray.dark[300],
      hover: alpha(Palette.white.self[100], 0.1),
      newChat: Palette.green.primary[700],
      text: Palette.white.self[100],
    },
    fab: {
      background: Palette.purple.reproduction[200],
      badge: Palette.purple.dark[400],
      badgeIcon: Palette.white.self[100],
      hover: darken(Palette.purple.reproduction[200], 0.2),
    },
    information: {
      accent: Palette.blue.self[500],
      message: Palette.blue.self[100],
    },
  },
  warning: Palette.orange.self[500],
} as const
